import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Image,
  ScrollView,
  Animated,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../../../../utility/AppColors";
import { AppFontStyle } from "../../../../../styles/AppFontStyle";
import { AppImages } from "../../../../../utility/AppImages";
import { PanGestureHandler } from "react-native-gesture-handler";
import { toast } from "react-toastify";
import * as Linking from "expo-linking";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { createSoloStreamingPaymentSession } from "../../../../../services/soloStreamingService";
import { getUserLocationDetails } from "../../../../../common/getLocation";

const { height: screenHeight, width: screenWidth } = Dimensions.get("window");

interface TokenPackage {
  tokens: number;
  price: string;
  exchangeRate: string;
}

interface TokenPurchaseBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  tokenPackages: TokenPackage[];
  selectedPackage: TokenPackage | null;
  handlePackageSelect: (pkg: TokenPackage) => void;
  streamId: string;
  streamUsername: string;
  variant?: "modal" | "popup";
}

/**
 * Local Decoupled TokenPurchaseBottomSheet for SoloStreaming.
 * Supports 'modal' (mobile bottom sheet) and 'popup' (desktop absolute overlay).
 */
const TokenPurchaseBottomSheet: React.FC<TokenPurchaseBottomSheetProps> = ({
  visible,
  onClose,
  tokenPackages,
  selectedPackage,
  handlePackageSelect,
  streamId,
  streamUsername,
  variant = "modal",
}) => {
  const [loading, setLoading] = useState(false);
  const slideAnim = useRef(new Animated.Value(screenHeight * 0.5)).current;

  useEffect(() => {
    if (visible && variant === "modal") {
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 10,
      }).start();
    } else if (variant === "modal") {
      Animated.timing(slideAnim, {
        toValue: screenHeight * 0.5,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, variant]);

  const handleGestureEnd = (event: any) => {
    // Disabled swipe-to-close
  };

  const handlePurchase = async () => {
    if (!selectedPackage) {
      toast.error("Please select a token package to purchase.");
      return;
    }

    setLoading(true);

    try {
      // Get access token from storage
      const loginDataJson = await AsyncStorage.getItem("@loginData");
      if (!loginDataJson) {
        toast.error("Please login to purchase tokens.");
        setLoading(false);
        return;
      }
      const loginData = JSON.parse(loginDataJson);
      // AccessToken might be capitalized or not depending on auth flow
      const accessToken = loginData?.AccessToken || loginData?.accessToken;

      if (!accessToken) {
        toast.error("Authentication required. Please login again.");
        setLoading(false);
        return;
      }

      // Get user location for tax purposes
      const location = await getUserLocationDetails();
      const countryCode = location?.country_code || "GB";

      console.log(
        `[TokenPurchase] Creating payment session for streamId: ${streamId}, username: ${streamUsername}`
      );
      console.log(
        `[TokenPurchase] Package: ${selectedPackage.tokens} tokens for ${selectedPackage.price}`
      );

      const sessionData = await createSoloStreamingPaymentSession(
        accessToken,
        selectedPackage.tokens,
        selectedPackage.price,
        streamId,
        streamUsername,
        countryCode
      );

      console.log("Payment session response:", sessionData);

      if (sessionData.statusCode === 303 && sessionData.body?.url) {
        Linking.openURL(sessionData.body.url);
        onClose(); // Close the modal after opening payment URL
      } else {
        const errorMsg =
          sessionData.body?.message ||
          sessionData.message ||
          "Error creating payment session. Please try again.";
        console.error("Payment session failed:", {
          statusCode: sessionData.statusCode,
          body: sessionData.body,
          fullResponse: sessionData,
        });
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Payment session error:", error);
      toast.error("Payment failed. Please check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderTokenPackage = ({
    item,
    index,
  }: {
    item: TokenPackage;
    index: number;
  }) => {
    const isSelected = selectedPackage?.tokens === item.tokens;
    const baseTokens = item.tokens > 88 ? item.tokens - 77 : item.tokens;
    const hasBonus = item.tokens > 88;
    const isPopup = variant === "popup";

    return (
      <View
        style={{
          width: isPopup
            ? undefined
            : screenWidth >= 100 || true
            ? "33.3%"
            : "48%",
          marginRight: isPopup ? 10 : 0,
        }}
        key={index}
      >
        <TouchableOpacity
          onPress={() => handlePackageSelect(item)}
          style={{ position: "relative" }}
        >
          {/* Badge image at top right */}
          {hasBonus && (
            <Image
              source={AppImages.Ko_Token_Bonus_Badge}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 45,
                height: 14,
                zIndex: 10,
                transform: [{ translateX: -8 }, { translateY: 8 }],
              }}
              resizeMode="contain"
            />
          )}

          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              borderRadius: 10,
              marginVertical: 2,
              marginHorizontal: 2,
              paddingVertical: isPopup ? 12 : 0, // Padding vertical for popup look
              paddingHorizontal: 8,
              borderWidth: isSelected ? 2 : 0.8,
              borderColor: isSelected
                ? AppColors.appViolentColor_6D08C3
                : "rgba(139, 147, 176, 0.10)",
              minHeight: isPopup ? 110 : 95, // Taller for popup
              minWidth: isPopup ? 140 : undefined,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                marginTop: hasBonus ? 8 : 0,
                width: "100%",
                justifyContent: "flex-start",
              }}
            >
              <Image
                source={AppImages.Ko_Token}
                style={{
                  height: isPopup ? 40 : 32,
                  width: isPopup ? 40 : 32,
                }}
              />
              <View style={{ gap: 2, flex: 1 }}>
                <Text
                  style={{
                    fontFamily: "Anybody_700Bold",
                    fontSize: isPopup ? 24 : 16,
                    fontWeight: "700",
                    lineHeight: isPopup ? 24 : 18,
                    color: AppColors.koBrandRed,
                  }}
                  numberOfLines={1}
                >
                  {item.tokens.toLocaleString()}
                </Text>
                {hasBonus && (
                  <Text
                    style={{
                      fontFamily: "Anybody_400Regular",
                      fontSize: 8,
                      fontWeight: "400",
                      lineHeight: 10,
                      color: AppColors.gray_8B93B0,
                    }}
                    numberOfLines={1}
                  >
                    {baseTokens.toLocaleString()} + 77
                  </Text>
                )}
              </View>
            </View>

            <Text
              style={{
                color: "#FFF",
                textAlign: "center",
                fontSize: isPopup ? 20 : 14,
                fontWeight: "700",
                lineHeight: isPopup ? 20 : 14,
              }}
              numberOfLines={1}
            >
              {item.price}
            </Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>
    );
  };

  // POPUP VARIANT (Desktop Overlay)
  if (variant === "popup") {
    if (!visible) return null;
    return (
      <View
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
        }}
      >
        <View style={{ position: "relative" }}>
          {/* Close Button Top Right */}
          <TouchableOpacity
            onPress={onClose}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1002,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: 20,
              width: 32,
              height: 32,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Image
              source={AppImages.icClose}
              style={{ width: 16, height: 16, tintColor: "white" }}
              resizeMode="contain"
            />
          </TouchableOpacity>

          <View
            style={{
              backgroundColor: "black",
              borderRadius: 10,
              borderWidth: 1,
              borderColor: "#374151",
            }}
          >
            <View
              style={{
                width: "100%",
                paddingBottom: 16,
                paddingHorizontal: 16,
                backgroundColor: "#111827",
                borderRadius: 10,
                justifyContent: "space-between",
              }}
            >
              <View style={{ backgroundColor: "#111827", paddingTop: 16 }}>
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_24,
                    { marginBottom: 10, color: AppColors.white },
                  ]}
                >
                  Get Tokens
                </Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={true}
                  contentContainerStyle={{
                    flexDirection: "row",
                    gap: 10,
                    marginBottom: 10,
                  }}
                >
                  {tokenPackages.map((item, index) =>
                    renderTokenPackage({ item, index })
                  )}
                </ScrollView>
              </View>

              <View>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_10,
                    {
                      color: AppColors.koBrandLightGray,
                      marginBottom: 8,
                    },
                  ]}
                >
                  By continuing you agree to Token Policy
                </Text>
                <TouchableOpacity
                  onPress={handlePurchase}
                  disabled={loading || !selectedPackage}
                  style={{
                    paddingVertical: 12,
                    backgroundColor: loading
                      ? "#6B7280"
                      : !selectedPackage
                      ? "#6B7280"
                      : "#DC2626",
                    borderRadius: 6,
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  {loading ? (
                    <ActivityIndicator size="small" color={AppColors.white} />
                  ) : (
                    <>
                      <Text
                        style={[
                          AppFontStyle.BOLD_ANYBODY_16,
                          { color: AppColors.white },
                        ]}
                      >
                        Get
                      </Text>
                      {selectedPackage && (
                        <Image
                          source={AppImages.Ko_Token}
                          style={{
                            height: 20,
                            width: 20,
                          }}
                          resizeMode="contain"
                        />
                      )}
                      <Text
                        style={[
                          AppFontStyle.BOLD_ANYBODY_16,
                          { color: AppColors.white },
                        ]}
                      >
                        {selectedPackage
                          ? `${selectedPackage.tokens.toLocaleString()} (${
                              selectedPackage.price
                            })`
                          : "Select a package"}
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  }

  // MODAL VARIANT (Mobile Bottom Sheet)
  return (
    <Modal transparent visible={visible} animationType="none">
      <View style={{ flex: 1 }}>
        <TouchableOpacity
          style={{ flex: 1 }}
          onPress={onClose}
          activeOpacity={1}
        />

        <PanGestureHandler onHandlerStateChange={handleGestureEnd}>
          <Animated.View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: screenHeight * 0.5,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              transform: [{ translateY: slideAnim }],
              overflow: "hidden",
            }}
          >
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                flex: 1,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
              }}
            >
              <View
                style={{
                  width: 40,
                  height: 4,
                  backgroundColor: AppColors.koBrandLightGray,
                  alignSelf: "center",
                  marginTop: 8,
                  borderRadius: 2,
                }}
              />

              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingHorizontal: 20,
                  paddingVertical: 12,
                }}
              >
                <Text
                  style={[AppFontStyle.BOLD_ANYBODY_18, { color: "white" }]}
                >
                  Get Tokens
                </Text>
              </View>

              <ScrollView
                style={{ paddingHorizontal: 5, flex: 1 }}
                contentContainerStyle={{ paddingBottom: 90 }}
                showsVerticalScrollIndicator={true}
              >
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    justifyContent: "flex-start",
                    marginBottom: 5,
                  }}
                >
                  {tokenPackages.map((item, index) =>
                    renderTokenPackage({ item, index })
                  )}
                </View>
              </ScrollView>

              <LinearGradient
                colors={["#111827", "#000000"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: 20,
                }}
              >
                <TouchableOpacity
                  onPress={handlePurchase}
                  disabled={!selectedPackage || loading}
                >
                  <LinearGradient
                    colors={
                      loading
                        ? ["#6B7280", "#6B7280"]
                        : !selectedPackage
                        ? ["#6B7280", "#6B7280"]
                        : ["#DC2626", "#991B1B"]
                    }
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={{
                      height: 38,
                      paddingVertical: 10,
                      paddingHorizontal: 24,
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "row",
                      gap: 10,
                      borderRadius: 6,
                    }}
                  >
                    {loading ? (
                      <ActivityIndicator size="small" color="white" />
                    ) : (
                      <>
                        {selectedPackage && (
                          <Image
                            source={AppImages.Ko_Token}
                            style={{ width: 20, height: 20 }}
                            resizeMode="contain"
                          />
                        )}
                        <Text
                          style={[
                            AppFontStyle.BOLD_ANYBODY_16,
                            { color: "white" },
                          ]}
                        >
                          {selectedPackage
                            ? `Get ${selectedPackage.tokens} (${selectedPackage.price})`
                            : "Select a package"}
                        </Text>
                      </>
                    )}
                  </LinearGradient>
                </TouchableOpacity>

                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_10,
                    {
                      color: AppColors.koBrandLightGray,
                      textAlign: "center",
                      marginTop: 8,
                    },
                  ]}
                >
                  By continuing you agree to Token Policy
                </Text>
              </LinearGradient>
            </LinearGradient>
          </Animated.View>
        </PanGestureHandler>
      </View>
    </Modal>
  );
};

export default TokenPurchaseBottomSheet;
